import React, { useState } from "react";
import { toast } from "sonner";
import { PlayCircle } from "lucide-react";
import { ruleReasons } from "../lib/api";
import { fmtMoney } from "../lib/labels";
import { useUsers } from "../lib/hooks";
import { ReasonsList } from "./common";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const FIN_ACTION_LABELS = {
  completar: "Completar información",
  enviar_revision: "Enviar a revisión",
  validar_presupuesto: "Validar presupuesto",
  solicitar_aprobacion: "Solicitar aprobación",
  solicitar_cambios: "Solicitar cambios",
  convertir_orden: "Convertir en orden",
  cancelar: "Cancelar",
  bloquear: "Bloquear",
  resolver_bloqueo: "Resolver bloqueo",
  desbloquear: "Desbloquear",
  revisar: "Revisar",
  preparar_emision: "Preparar emisión",
  emitir: "Emitir al proveedor",
  registrar_confirmacion: "Registrar confirmación",
  registrar_recepcion_proveedor: "Registrar recepción del proveedor",
  cerrar: "Cerrar",
  validar_extraccion: "Validar extracción",
  ejecutar_matching: "Ejecutar matching",
  solicitar_informacion: "Solicitar información",
  preparar_pago: "Preparar para pago",
  rechazar: "Rechazar",
  disputar: "Disputar",
  anular: "Anular",
  archivar: "Archivar",
  actualizar_control: "Actualizar fecha de control",
  publicar: "Publicar versión",
  descartar: "Descartar versión",
  analizar: "Analizar riesgo",
  accionar: "Iniciar acción",
  mitigar: "Marcar mitigado",
  incorporar: "Incorporar al planning",
  iniciar_cierre: "Iniciar cierre",
  pausar: "Poner en pausa",
  reanudar: "Reanudar",
  marcar_riesgo: "Marcar en riesgo",
  quitar_riesgo: "Quitar riesgo",
  iniciar: "Iniciar preparación",
  lista_revision: "Marcar lista para revisión",
  solicitar_revision: "Solicitar revisión del gate",
  retomar: "Retomar preparación",
  no_aplicable: "Marcar no aplicable",
  validar: "Validar",
  suspender: "Suspender",
  aceptar: "Aceptar",
  con_reservas: "Aceptar con reservas",
  asignar: "Asignar",
  resolver: "Resolver",
  aceptar_excepcionalmente: "Aceptar excepcionalmente",
  escalar: "Escalar",
  caducar: "Marcar caducada",
};

const NEED_LABELS = {
  motivo: "Motivo",
  resolucion: "Resolución aplicada",
  confirmacion: "Detalle de la confirmación del proveedor",
  confirm: "Confirmación humana",
  justification: "Justificación",
  justificacion: "Justificación",
  retained_amount: "Importe retenido (€)",
  amount: "Importe (€)",
  owner_id: "Asignar a",
  evidence: "Evidencia / referencia",
  comentario: "Comentario",
  cause: "Causa raíz",
  extraordinary_approval_id: "ID de autorización extraordinaria",
};

const SECONDARY_ACTIONS = new Set(["no_aplicable", "archivar", "cancelar", "bloquear", "suspender", "rechazar", "anular", "desbloquear", "caducar", "disputar"]);

/** Botonera de acciones controladas: ejecuta, pide campos requeridos y explica bloqueos. */
export function FinActions({ actions, onAct, testid = "fin-actions" }) {
  const { users } = useUsers();
  const [blocked, setBlocked] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  if (!actions?.length) return null;
  // Oculta acciones incompatibles con el estado actual (solo razón «estado»); muestra las bloqueadas por requisitos reales
  const visible = actions.filter((a) => a.permission && (a.allowed || (a.reasons || []).some((r) => r.code !== "estado")));

  const run = async (action, payload = {}) => {
    setBusy(true);
    try {
      await onAct(action, payload);
      setDialog(null);
      setFields({});
    } catch (e) {
      const reasons = ruleReasons(e);
      if (reasons) setBlocked({ action, reasons });
      else toast.error(e?.response?.data?.detail || "La acción no se pudo ejecutar");
    } finally {
      setBusy(false);
    }
  };

  const needs = dialog?.needs || [];

  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {visible.map((a) => (
        <Button
          key={a.action}
          size="sm"
          variant={a.allowed && !SECONDARY_ACTIONS.has(a.action) ? "default" : "outline"}
          className={!a.allowed ? "border-amber-300 text-amber-700 hover:bg-amber-50" : ""}
          data-testid={`${testid}-${a.action}`}
          disabled={busy}
          onClick={() => {
            if (!a.allowed) return setBlocked({ action: a.action, reasons: a.reasons });
            if (a.needs?.length) {
              setFields({});
              setDialog(a);
            } else {
              run(a.action);
            }
          }}
        >
          <PlayCircle className="mr-1 h-3.5 w-3.5" />
          {FIN_ACTION_LABELS[a.action] || a.action}
        </Button>
      ))}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{FIN_ACTION_LABELS[dialog?.action] || dialog?.action}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {needs.map((f) =>
              f === "confirm" ? (
                <label key={f} className="flex items-start gap-2 rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm" data-testid={`${testid}-field-confirm`}>
                  <input type="checkbox" className="mt-0.5" checked={!!fields.confirm} onChange={(e) => setFields({ ...fields, confirm: e.target.checked })} />
                  Confirmo que he revisado proveedor, importe, alcance, partida presupuestaria y condiciones. Esta acción queda registrada.
                </label>
              ) : f === "owner_id" ? (
                <div key={f}>
                  <Label>{NEED_LABELS[f]}</Label>
                  <Select value={fields.owner_id || ""} onValueChange={(v) => setFields({ ...fields, owner_id: v })}>
                    <SelectTrigger data-testid={`${testid}-field-owner`}><SelectValue placeholder="Selecciona responsable…" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : f === "retained_amount" || f === "amount" ? (
                <div key={f}>
                  <Label>{NEED_LABELS[f]}</Label>
                  <Input type="number" value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} data-testid={`${testid}-field-${f}`} />
                </div>
              ) : (
                <div key={f}>
                  <Label>{NEED_LABELS[f] || f}</Label>
                  <Textarea value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} data-testid={`${testid}-field-${f}`} />
                </div>
              )
            )}
            <Button
              className="w-full"
              data-testid={`${testid}-confirm`}
              disabled={busy || needs.some((f) => f === "confirm" ? !fields.confirm : !String(fields[f] || "").trim())}
              onClick={() => run(dialog.action, { ...fields, retained_amount: fields.retained_amount ? Number(fields.retained_amount) : undefined, amount: fields.amount ? Number(fields.amount) : undefined })}
            >
              {busy ? "Ejecutando…" : "Confirmar acción"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!blocked} onOpenChange={() => setBlocked(null)}>
        <DialogContent data-testid={`${testid}-blocked-dialog`}>
          <DialogHeader><DialogTitle>Acción bloqueada — {FIN_ACTION_LABELS[blocked?.action] || blocked?.action}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Esta acción no puede ejecutarse todavía. Esto es lo que falta y quién debe resolverlo:</p>
          <ReasonsList reasons={blocked?.reasons || []} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Money({ value, currency = "EUR", className = "" }) {
  return <span className={`font-mono ${className}`}>{fmtMoney(value, currency)}</span>;
}

export function DemoBanner() {
  return (
    <p className="rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="demo-banner">
      Entorno demo: conectores ERP/banco/email simulados. DEMO MODE — NO PAYMENT EXECUTED. Ningún documento tiene validez fiscal o bancaria.
    </p>
  );
}
